import { getManualAllocationPayments, type ManualAllocationListItem } from '@urbanik/api-client';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';

const reviewedKey = 'urbanik:admin-reviewed-payments';
const readReviewed = (): string[] => {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(reviewedKey) ?? '[]');
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
};

export function useAdminPaymentNotifications(enabled: boolean) {
  const [items, setItems] = useState<readonly ManualAllocationListItem[]>([]);
  const [reviewed, setReviewed] = useState<readonly string[]>(readReviewed);

  useEffect(() => {
    if (!enabled) {
      setItems([]);
      return;
    }
    let active = true;
    const refresh = () =>
      getManualAllocationPayments(apiBase)
        .then((result) => {
          if (active) setItems(result.items);
        })
        .catch(() => undefined);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5_000);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('urbanik:payment-draft-submitted', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('urbanik:payment-draft-submitted', refresh);
    };
  }, [enabled]);

  const unread = useMemo(
    () => items.filter((item) => !reviewed.includes(item.id)),
    [items, reviewed],
  );
  const review = (payment: ManualAllocationListItem) => {
    const next = Array.from(new Set([...reviewed, payment.id])).slice(-100);
    localStorage.setItem(reviewedKey, JSON.stringify(next));
    setReviewed(next);
    window.location.hash = '#pagos';
    window.dispatchEvent(new CustomEvent('urbanik:refresh-admin-payments'));
  };
  const notifications = unread.length ? (
    <div>
      {unread.slice(0, 5).map((payment) => (
        <article className="ui-notifications__item" key={payment.id}>
          <strong>Nuevo comprobante por revisar</strong>
          <p>
            {payment.memberName} registró el comprobante {payment.reference} por USD{' '}
            {Number(payment.amount).toFixed(2)}.
          </p>
          <div className="ui-notifications__actions">
            <button
              type="button"
              className="ui-button ui-button--primary"
              onClick={() => review(payment)}
            >
              Ver comprobante
            </button>
          </div>
        </article>
      ))}
      {unread.length > 5 ? (
        <p>Hay {unread.length - 5} comprobantes adicionales pendientes.</p>
      ) : null}
    </div>
  ) : undefined;

  return { count: unread.length, notifications };
}
